/**
 * Hand-written GraphQL operations for `photovogue-api` admin reads.
 *
 * NOT codegen: the web app's codegen is bound to the Open Calls `apps/api/schema.gql`,
 * so PhotoVogue operations are plain strings sent with `graphql-request` against the
 * separate `photovogue-api` GraphQL endpoint (see ConfigService.PHOTOVOGUE_GRAPHQL_ENDPOINT).
 *
 * Field notes (from the photovogue-api integration spec):
 * - `tags`/`themes`/`types` on PhotoEntity are runtime resolver fields.
 * - image URL field `privateImage` maps to our `imageUrls.private`.
 * - Photographer has no single `name` column — concat `first_name`+`last_name` client-side.
 */

const PHOTO_NODE = `
  id
  title
  description
  created_at
  state_updated_at
  category { id name }
  state { id name }
  photographer { id first_name last_name }
  tags { id text }
  themes { id text }
  types { id name }
  imageUrls { thumbnail small smallFit medium large gallery privateImage }
`;

const PHOTOGRAPHER_NODE = `
  id
  created_at
  first_name
  last_name
  avatar
  city
  country { id name }
  state { id name }
  isGold
  warning_count
  ban_count
  total_photos
  total_best
  total_daily
`;

export const SEARCH_ADMIN_PHOTOS = `
query SearchAdminPhotos($input: AdminPhotoSearchInput!) {
  searchAdminPhotos(input: $input) {
    items { ${PHOTO_NODE} }
    page
    pageSize
    totalItems
    totalPages
    hasNext
  }
}`;

export const GET_PHOTO_BY_ID = `
query GetPhotoById($input: GetPhotoByIdInput!) {
  getPhotoById(input: $input) { ${PHOTO_NODE} }
}`;

export const SEARCH_ADMIN_PHOTOGRAPHERS = `
query SearchAdminPhotographers($input: AdminPhotographerSearchInput!) {
  searchAdminPhotographers(input: $input) {
    items { ${PHOTOGRAPHER_NODE} }
    page
    pageSize
    totalItems
    totalPages
    hasNext
  }
}`;

export const GET_PHOTOGRAPHER_BY_ID = `
query GetPhotographerById($input: GetPhotographerByIdInput!) {
  getPhotographerById(input: $input) { ${PHOTOGRAPHER_NODE} }
}`;

// Dictionaries — used to resolve name -> id for writes and to label filters.
export const GET_ALL_PHOTO_STATES = `
query GetAllPhotoStates($first: Int!, $after: String) {
  getAllPhotoStates(first: $first, after: $after) {
    edges { node { id name } }
    pageInfo { endCursor hasNextPage }
  }
}`;

export const GET_ALL_PHOTOGRAPHER_STATES = `
query GetAllPhotographerStates($first: Int!, $after: String) {
  getAllPhotographerStates(first: $first, after: $after) {
    edges { node { id name } }
    pageInfo { endCursor hasNextPage }
  }
}`;

export const GET_ALL_PHOTO_TYPES = `
query GetAllPhotoType($first: Int!, $after: String) {
  getAllPhotoType(first: $first, after: $after) {
    edges { node { id name } }
    pageInfo { endCursor hasNextPage }
  }
}`;

export const GET_ALL_CATEGORIES = `
query GetAllCategories($first: Int!, $after: String) {
  getAllCategories(first: $first, after: $after) {
    edges { node { id name } }
    pageInfo { endCursor hasNextPage }
  }
}`;

export const GET_ALL_THEMES = `
query GetAllThemes($first: Int!, $after: String) {
  getAllThemes(first: $first, after: $after) {
    edges { node { id text } }
    pageInfo { endCursor hasNextPage }
  }
}`;

export const GET_ALL_TAGS = `
query GetAllTags($first: Int!, $after: String) {
  getAllTags(first: $first, after: $after) {
    edges { node { id text } }
    pageInfo { endCursor hasNextPage }
  }
}`;

export const GET_ALL_COUNTRIES = `
query GetAllCountries($first: Int!, $after: String) {
  getAllCountries(first: $first, after: $after) {
    edges { node { id name } }
    pageInfo { endCursor hasNextPage }
  }
}`;
